import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiCar, FiMessageSquare, FiLogOut, FiMenu, FiX, FiPlus, FiUsers } = FiIcons;

const AdminLayout = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: FiHome },
    { name: 'Vehículos', href: '/admin/vehiculos', icon: FiCar },
    { name: 'Consultas', href: '/admin/consultas', icon: FiMessageSquare },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#2c3e50] transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}
      >
        <div className="flex items-center justify-center h-16 bg-[#34495e] border-b border-gray-600">
          <Link to="/">
            <img src="https://i.imgur.com/lRJEIRp.png" alt="AUTOSFERSAN Logo" className="h-10" />
          </Link>
        </div>
        <nav className="mt-8">
          <div className="px-4 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#3498db] text-white'
                      : 'text-gray-300 hover:bg-[#34495e] hover:text-white'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="mr-3 w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
          </div>
          <div className="mt-8 px-4">
            <div className="border-t border-gray-600 pt-4">
              <Link
                to="/admin/vehiculos/nuevo"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center px-4 py-3 text-sm font-medium text-white bg-[#e67e22] hover:bg-orange-600 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiPlus} className="mr-3 w-5 h-5" />
                Añadir Vehículo
              </Link>
            </div>
          </div>
        </nav>
        <div className="absolute bottom-0 w-full p-4">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-300 hover:bg-[#34495e] hover:text-white rounded-lg transition-colors"
          >
            <SafeIcon icon={FiLogOut} className="mr-3 w-5 h-5" />
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black opacity-25 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-0">
        {/* Top Bar */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-gray-500 hover:text-gray-600 lg:hidden"
              >
                <SafeIcon icon={sidebarOpen ? FiX : FiMenu} className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-4">
                <Link
                  to="/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#005c91] hover:text-[#3498db] font-medium"
                >
                  Ver Sitio Web
                </Link>
                <div className="flex items-center space-x-2 text-gray-600">
                  <SafeIcon icon={FiUsers} className="w-5 h-5" />
                  <span>Administrador</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Page Content */}
        <main className="flex-1">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;